import React from "react";
import AnimationRevealPage from "../../../../helpers/AnimationRevealPage.js";
import tw from "twin.macro";
import Header from "../../../../Components/headers/PageHeader";
import MainFeature1 from "../../../../Components/Features/TwoColWithButton.js";
import Features from "../../../../Components/Features/ThreeColumnPages";
import {
  FaVideo,
  FaEye,
  FaRocket,
  FaMobileAlt,
  FaStar,
  FaYoutube,
} from "react-icons/fa";

// Styled Components
const Subheading = tw.span`uppercase tracking-wider text-sm`;
const Heading = tw.h1`text-2xl md:text-4xl lg:text-5xl font-bold`;
const Description = tw.p`text-base md:text-lg lg:text-xl`;

export const YouTubeVideoProductionInAlBarsha = () => {
  return (
    <AnimationRevealPage>
      {/* Header Section */}
      <Header from="YouTube Video Production in Al Barsha" />

      {/* Hero Section */}
      <MainFeature1
        subheading={<Subheading>Grow Your Channel</Subheading>}
        heading={
          <Heading>
            Professional{" "}
            <span tw="text-primary-500">YouTube Video Production</span> in Al
            Barsha
          </Heading>
        }
        description={
          <Description>
            At MilaajBrandset, our YouTube video production services in **Al
            Barsha** create engaging, high-quality videos that boost your
            channel’s visibility and subscriber base. From vlogs to tutorials,
            we craft content that captivates and converts viewers.
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1609921141835-710b7fa6e438?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Video production setup
          "https://images.unsplash.com/photo-1504465039542-a4308c88248f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // YouTube creative filming
          "https://images.unsplash.com/photo-1492619375914-88005aa9e8fb?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Content creation with Dubai background
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Start Your Video"
        buttonRounded={true}
        textOnLeft={false}
      />

      {/* Key Benefits Section */}
      <Features
        subheading={<Subheading>Why Choose Us?</Subheading>}
        heading="Expert YouTube Video Production in Al Barsha"
        description="We create YouTube content that engages and grows your audience."
        cards={[
          {
            icon: <FaYoutube color="#ff5464" size={40} />,
            title: "YouTube-Optimized",
            description:
              "Videos tailored for YouTube’s algorithm and audience.",
          },
          {
            icon: <FaEye color="#ff5464" size={40} />,
            title: "Engaging Content",
            description: "Compelling visuals that keep viewers watching.",
          },
          {
            icon: <FaRocket color="#ff5464" size={40} />,
            title: "Fast Delivery",
            description: "Quick turnaround to keep your channel active.",
          },
          {
            icon: <FaMobileAlt color="#ff5464" size={40} />,
            title: "Mobile-Friendly",
            description: "Content optimized for mobile and desktop viewers.",
          },
          {
            icon: <FaStar color="#ff5464" size={40} />,
            title: "Trusted Expertise",
            description: "Al Barsha’s choice for YouTube production.",
          },
          {
            icon: <FaVideo color="#ff5464" size={40} />,
            title: "End-to-End Service",
            description: "From scripting to editing, we handle it all.",
          },
        ]}
      />

      {/* Our Process Section */}
      <MainFeature1
        subheading={<Subheading>How We Work</Subheading>}
        heading="Our YouTube Video Production Process"
        description={
          <Description>
            We deliver high-impact YouTube videos through a streamlined process:
            <ul tw="mt-4 list-disc pl-6">
              <li>
                Consultation: Align with your channel’s goals and audience.
              </li>
              <li>Scripting: Craft a compelling script or storyboard.</li>
              <li>
                Filming: Capture high-quality footage with professional
                equipment.
              </li>
              <li>
                Editing: Enhance with effects, music, and YouTube-specific
                optimizations.
              </li>
              <li>Review: Ensure the video meets your vision and goals.</li>
              <li>
                Delivery: Provide ready-to-upload content with optimized
                metadata.
              </li>
            </ul>
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1551847677-dc82d764e1eb?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Content planning
          "https://images.unsplash.com/photo-1574375927938-d5a98e8ffe85?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Video editing
          "https://images.unsplash.com/photo-1533228876829-65c94e7b5025?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Camera equipment filming
        ]}
        widths={["60%", "55%", "70%"]}
        heights={["60%", "45%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "right: 30%; top: 10%;",
          "left: 35%; bottom: 20%;",
          "right: 32%; bottom: 10%;",
        ]}
        primaryButtonText="Learn More"
        buttonRounded={true}
        textOnLeft={true}
      />

      {/* Success Stories Section */}
      <Features
        subheading={<Subheading>Success Stories</Subheading>}
        heading="YouTube Video Wins in Al Barsha"
        description="Our YouTube videos have driven growth for channels in Al Barsha."
        cards={[
          {
            icon: <FaVideo color="#ff5464" size={40} />,
            title: "Increased Subscribers",
            description: "Grew a channel’s audience with engaging tutorials.",
          },
          {
            icon: <FaEye color="#ff5464" size={40} />,
            title: "Boosted Views",
            description: "Doubled viewership with a viral vlog series.",
          },
          {
            icon: <FaStar color="#ff5464" size={40} />,
            title: "Client Satisfaction",
            description: "95% approval from YouTube content clients.",
          },
        ]}
      />

      {/* Call-to-Action Section */}
      <MainFeature1
        subheading={<Subheading>Ready to Grow Your Channel?</Subheading>}
        heading="YouTube Video Production in Al Barsha"
        description={
          <Description>
            Partner with MilaajBrandset for professional YouTube video
            production in Al Barsha. Create engaging, high-quality content that
            grows your audience and boosts your channel—contact us today!
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1626368305730-9461fba72d6d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Professional filming
          "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Content creation
          "https://images.unsplash.com/photo-1611162616475-46b635cb6868?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Video production setup
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Contact Us Now"
        buttonRounded={true}
        textOnLeft={false}
      />
    </AnimationRevealPage>
  );
};
